import { useCallback, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  Button,
  TextField,
} from '@mui/material';

import { patchFlagMessage, deleteFlag } from 'api/flags';
import { getErrorDetail } from 'helpers';
import { useSnackContext } from 'hooks/useSnackContext';
import { FullFlag, ReviewedFullCandidateProps } from 'types';

export interface FlagCardProps {
  flag: FullFlag;
  setCandidate: React.Dispatch<
    React.SetStateAction<ReviewedFullCandidateProps>
  >;
}

export const FlagCard = (props: FlagCardProps) => {
  const { flag, setCandidate } = props;
  const { type = 'unknown_type', message, id } = flag;

  const [messageState, setMessageState] = useState(message);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deletingFlag, setDeletingFlag] = useState(false);

  const { setSnack } = useSnackContext();

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      const { data: newFlag } = await patchFlagMessage(id, messageState);
      setCandidate((prev) => {
        const index = prev.flags.findIndex(({ id }) => id === newFlag.id);

        return index === -1
          ? prev
          : {
              ...prev,
              flags: [
                ...prev.flags.slice(0, index),
                newFlag,
                ...prev.flags.slice(index + 1),
              ],
            };
      });
      setEditing(false);
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: getErrorDetail({
          error: e,
          fallback: 'Failed To Submit Flag',
        }),
        error: e as Error,
      }));
    } finally {
      setLoading(false);
    }
  }, [id, messageState, setCandidate, setSnack]);

  const handleDeleteFlag = useCallback(async () => {
    setDeletingFlag(true);
    try {
      await deleteFlag(id);
      setCandidate((prev) => ({
        ...prev,
        flags: prev.flags.filter((flag) => flag.id !== id),
      }));
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: getErrorDetail({
          error: e,
          fallback: 'Failed To Delete Flag',
        }),
        error: e as Error,
      }));
    } finally {
      setDeletingFlag(false);
    }
  }, [id, setCandidate, setSnack]);

  const handleEdit = () => {
    setEditing(true);
  };

  return (
    <Card sx={{ width: '200px', mr: 1 }}>
      <CardHeader
        sx={{ backgroundColor: '#f0f0f0' }}
        titleTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
        title={type.replaceAll('_', ' ').toUpperCase()}
        action={
          editing ? (
            <LoadingButton
              loading={deletingFlag}
              onClick={handleDeleteFlag}
              color='error'
            >
              DELETE
            </LoadingButton>
          ) : (
            <div />
          )
        }
      />
      <CardContent
        sx={{
          display: 'flex',
          flexFlow: 'column',
        }}
      >
        {!editing ? (
          <Typography variant='body2'>{message}</Typography>
        ) : (
          <TextField
            hiddenLabel
            variant='outlined'
            multiline
            rows={4}
            disabled={!editing}
            value={messageState}
            onChange={({ target: { value } }) => setMessageState(value)}
          />
        )}
      </CardContent>
      <CardActions>
        {!editing ? (
          <Button size='small' onClick={handleEdit} variant='text'>
            EDIT
          </Button>
        ) : (
          <>
            <LoadingButton
              size='small'
              loading={loading}
              onClick={handleSubmit}
              variant='contained'
            >
              SAVE
            </LoadingButton>
            <Button
              size='small'
              onClick={() => setEditing(false)}
              variant='text'
            >
              CANCEL
            </Button>
          </>
        )}
      </CardActions>
    </Card>
  );
};
